import { useMemo, useState } from 'react';
import ActionLink from '../../../../Components/Dashboard/ActionLink';
import Card from '../../../../Components/Dashboard/Card';
import { CardEmpty, CardError, CardLoading } from '../../../../Components/Dashboard/CardState';
import API from '../../../../Utils/api';
import useAsync from '../../../../Hooks/useAsync';
import PerformanceChart from '../../PerformancePage/PerformanceChart';
import ReadinessChart from '../../PerformancePage/ReadinessChart';
import { getAcwrWarmupWindows } from '../../PerformancePage/acwrWarmup';
import {
    buildChartDataSource,
    buildPerformanceSeries,
    monthShortcutRange,
    prepareWorkouts
} from '../../PerformancePage/performanceData';
import styles from './performancePreview.module.css';

const TITLE = 'Performance preview';

// Read-only view of the existing Performance pipeline for its one-calendar-month shortcut. No load maths here.
const PerformancePreviewCard = ({ athleteId, performancePath = '/performance', now: nowProp }) => {
    const [mountedAt] = useState(() => new Date());
    const now = nowProp || mountedAt;
    const { status, data, error, reload } = useAsync(
        () => API.getAllUserWorkoutTypes(athleteId).then(res => prepareWorkouts(res.data)),
        [athleteId]
    );

    const view = useMemo(() => {
        if (!data || data.length === 0) return null;
        const { filteredData } = buildPerformanceSeries(data, monthShortcutRange(now));
        const hasData = filteredData.some(
            entry => entry.workoutCount > 0 || entry.isAssigned || entry.status === 'Assigned'
        );
        if (!hasData) return null;
        return {
            filteredData,
            chartDataSource: buildChartDataSource(filteredData, true),
            acwrWarmupWindows: getAcwrWarmupWindows(data)
        };
    }, [data, now]);

    let body;
    if (status === 'loading' && !data) body = <CardLoading label={TITLE} />;
    else if (status === 'error') body = <CardError label={TITLE} status={error?.response?.status} onRetry={reload} />;
    else if (!view) body = <CardEmpty>No completed or planned workouts to chart yet.</CardEmpty>;
    else
        body = (
            <div className={styles.charts}>
                <section className={styles.chart} aria-labelledby="pp-load">
                    <h3 id="pp-load" className={styles.heading}>Fitness, fatigue and form</h3>
                    <PerformanceChart chartDataSource={view.chartDataSource} />
                </section>
                <section className={styles.chart} aria-labelledby="pp-ready">
                    <h3 id="pp-ready" className={styles.heading}>Readiness and ACWR</h3>
                    <ReadinessChart
                        chartDataSource={view.chartDataSource}
                        filteredData={view.filteredData}
                        acwrWarmupWindows={view.acwrWarmupWindows}
                    />
                </section>
            </div>
        );

    return (
        <Card
            id="pp"
            module="athlete-performance-preview"
            title={TITLE}
            sub="Last month"
            busy={status === 'loading'}
            right={<ActionLink to={performancePath}>Open Performance</ActionLink>}
        >
            {body}
        </Card>
    );
};

export default PerformancePreviewCard;
